import React, { useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useShallow } from "zustand/react/shallow";
import { Button } from "components/common/Button";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { respondToOrgInvitation } from "@/lib/api/organizations";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

const message = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

/// Pending org invitations awaiting the user's answer (D43) — one row per
/// invite, rendered where they land after login. The bell card is the
/// secondary surface; this one also serves a fresh registrant, whose invite
/// predates their account and so never produced a notification.
export const OrgInviteBanner: React.FC = () => {
  const { pendingInvites, refreshInvites, refreshOrgs, switchOrg } =
    useOrgContextStore(
      useShallow((s) => ({
        pendingInvites: s.pendingInvites,
        refreshInvites: s.refreshInvites,
        refreshOrgs: s.refreshOrgs,
        switchOrg: s.switchOrg,
      })),
    );
  const [busyOrgId, setBusyOrgId] = useState<string | null>(null);

  if (pendingInvites.length === 0) return null;

  const respond = async (orgId: string, orgName: string, accept: boolean) => {
    setBusyOrgId(orgId);
    try {
      await respondToOrgInvitation(orgId, accept);
      await Promise.all([refreshInvites(), refreshOrgs()]);
      if (accept) {
        showNotificationGlobal(`You've joined ${orgName}`, "success", {
          label: "Switch",
          onClick: () => void switchOrg(orgId),
        });
      } else {
        showNotificationGlobal("Invitation declined", "success");
      }
    } catch (error) {
      showNotificationGlobal(
        message(error, "Could not respond to the invitation"),
        "error",
      );
      // The refusal may mean the invite was settled elsewhere — re-sync.
      void refreshInvites();
    } finally {
      setBusyOrgId(null);
    }
  };

  return (
    <div className="mt-6 flex flex-col gap-2">
      {pendingInvites.map((invite) => (
        <div
          key={invite.org_id}
          role="note"
          className="border-accent bg-list-selected-light flex items-center gap-2 rounded-app border p-3"
        >
          <EmailOutlinedIcon className="icon-md text-accent shrink-0" />
          <span className="text-fg-regular min-w-0 flex-1 truncate">
            <span className="text-fg-medium">
              {invite.inviter_name || "Someone"}
            </span>{" "}
            invited you to join{" "}
            <span className="text-fg-medium">{invite.org_name}</span>
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={busyOrgId === invite.org_id}
            onClick={() => void respond(invite.org_id, invite.org_name, false)}
          >
            Decline
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busyOrgId === invite.org_id}
            onClick={() => void respond(invite.org_id, invite.org_name, true)}
          >
            Accept
          </Button>
        </div>
      ))}
    </div>
  );
};
